(ns io.daskul.webapp.widgets.dashboard.metric-grid
  "Metric grid — dispatches on :widget to render the correct dashboard component.

   Renders a responsive grid of metric widgets. Each metric map must have a
   :widget key (:stat, :distribution, :ranked-table, :alert, :time-series).

   Full-width widgets (time-series, ranked-table) render outside the grid.

   Actionable metrics (`:actionable true`) with zero value are hidden.
   When all actionable metrics are zero, shows a positive 'all caught up' message.
   Actionable and informational metrics render in separate groups."
  (:require
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.dashboard.stat-card :as stat-card]
   [io.daskul.webapp.widgets.dashboard.stat-group :as stat-group]
   [io.daskul.webapp.widgets.dashboard.alert-card :as alert-card]
   [io.daskul.webapp.widgets.dashboard.distribution-chart :as distribution-chart]
   [io.daskul.webapp.widgets.dashboard.ranked-table :as ranked-table]
   [io.daskul.webapp.widgets.dashboard.time-series-chart :as time-series-chart]
   [io.daskul.webapp.widgets.dashboard.pnl-summary :as pnl-summary]
   [io.daskul.webapp.widgets.dashboard.enrollment-movement :as enrollment-movement]
   [io.daskul.webapp.widgets.dashboard.ageing-bar :as ageing-bar]
   [io.daskul.webapp.widgets.dashboard.program-table :as program-table]
   [io.daskul.webapp.widgets.dashboard.item-list :as item-list]))

(def ^:private stat-like-widgets
  "Widgets that sit in the stat-card grid (compact KPI cards)."
  #{:stat :pnl-summary})

(def ^:private full-width-widgets
  "Widgets that render outside the stat-card grid."
  #{:time-series :ranked-table :stat-group :item-list :table :enrollment-movement :ageing-bar :program-table})

(def ^:private half-width-widgets
  "Full-width widgets that pair well in a 2-column grid."
  #{:item-list :table})

(defui metric-widget
  "Dispatch a single metric map to the correct widget component."
  [metric]
  (case (keyword (:widget metric))
    :stat         ($ stat-card/stat-card metric)
    :stat-group   ($ stat-group/stat-group metric)
    :distribution ($ distribution-chart/distribution-chart metric)
    :ranked-table ($ ranked-table/ranked-table metric)
    :pnl-summary  ($ pnl-summary/pnl-summary metric)
    :enrollment-movement ($ enrollment-movement/enrollment-movement metric)
    :ageing-bar   ($ ageing-bar/ageing-bar metric)
    :program-table ($ program-table/program-table metric)
    :alert        ($ alert-card/alert-card metric)
    :time-series  ($ time-series-chart/time-series-chart metric)
    :item-list    ($ item-list/item-list metric)
    :table        ($ item-list/item-list metric)
    ;; Unknown widget — render nothing
    nil))

(defn- actionable-zero?
  "True if metric is actionable with zero value (should be hidden)."
  [metric]
  (and (:actionable metric) (zero? (:value metric))))

(defn- empty-table?
  "True if metric is a table/list with no items (should be hidden)."
  [metric]
  (and (half-width-widgets (keyword (:widget metric)))
       (empty? (:items metric))))

(defn- widget-type [metric]
  (keyword (:widget metric)))

(defui metric-grid
  "Render metrics with responsive layout.

   When a section has both stat cards and table/list widgets, renders a
   two-column layout: stat cards on the left (2-col sub-grid), tables on
   the right (stacked). On mobile, stacks vertically.

   Actionable metrics with zero value are hidden. When all actionable
   metrics are zero, shows a positive 'all caught up' message.
   Tables with no items are also hidden."
  [{:keys [metrics]}]
  (let [visible     (vec (remove #(or (actionable-zero? %) (empty-table? %)) metrics))
        stats       (filterv #(stat-like-widgets (widget-type %)) visible)
        tables      (filterv #(half-width-widgets (widget-type %)) visible)
        other       (filterv #(and (not (stat-like-widgets (widget-type %)))
                                   (not (half-width-widgets (widget-type %)))
                                   (not (full-width-widgets (widget-type %)))) visible)
        wide        (filterv #(and (full-width-widgets (widget-type %))
                                   (not (half-width-widgets (widget-type %)))) visible)
        ;; Stat cards keep a consistent ~quarter-row width (4-up) rather than
        ;; stretching to fill however many there are — so a 2-card P&L reads as
        ;; two normal cards with space to the right, not two half-page slabs.
        stat-lg     (if (>= (count stats) 2) 4 (count stats))]
    (if (empty? visible)
      ($ ui/text {:size "sm" :c "dimmed"} "All clear here.")
      ($ ui/stack {:gap "md"}

      (cond
        ;; Both stats and tables: two-column layout
        (and (seq stats) (seq tables))
        ($ ui/grid {:gutter "md"}
          ($ ui/grid-col {:span #js {:base 12 :md 6}}
            ($ ui/simple-grid {:cols 2 :spacing "md"}
              (for [m stats]
                ($ :div {:key (:key m)} ($ metric-widget m)))))
          ($ ui/grid-col {:span #js {:base 12 :md 6}}
            ($ ui/stack {:gap "md"}
              (for [m tables]
                ($ :div {:key (:key m)} ($ metric-widget m))))))

        ;; Stats only — one row (stat-cols wide), cells stretched to equal height
        ;; (each card is a flex child that fills the grid track).
        (seq stats)
        ($ ui/simple-grid {:cols #js {:base 1 :sm 2 :lg stat-lg} :spacing "md"}
          (for [m stats]
            ($ :div {:key (:key m)} ($ metric-widget m))))

        ;; Tables only
        (seq tables)
        ($ ui/simple-grid {:cols #js {:base 1 :sm 2} :spacing "md"}
          (for [m tables]
            ($ :div {:key (:key m)} ($ metric-widget m)))))

      ;; Grid-sized widgets (distribution, etc.)
      (when (seq other)
        ($ ui/simple-grid {:cols #js {:base 1 :sm 2 :lg 3} :spacing "md"}
          (for [m other]
            ($ :div {:key (:key m)} ($ metric-widget m)))))

      ;; Full-width widgets; anything tagged :half-width pairs into a 2-col row
      ;; (e.g. By Branch + Expenses by Category side by side).
      (for [m (filterv #(not (:half-width %)) wide)]
        ($ :div {:key (:key m)} ($ metric-widget m)))
      (when-let [half-wide (seq (filterv :half-width wide))]
        ($ ui/simple-grid {:cols #js {:base 1 :md 2} :spacing "md"}
          (for [m half-wide]
            ($ :div {:key (:key m)} ($ metric-widget m)))))))))
